import { useEffect, useMemo, useState } from "react";
import { ControllerFieldState, ControllerRenderProps } from "react-hook-form";
import TextField from "@material-ui/core/TextField";
import Autocomplete from "@material-ui/lab/Autocomplete";
import CircularProgress from "@material-ui/core/CircularProgress";
import { usePriorityList } from "../../../../../services/api-tansquery/priority/list";
import { Priority } from "../../../../../@types/priority";
import { useDebouncedValue } from "../../../../../hooks/useDebounce";

type Priorities = Array<
    Pick<
        Priority,
        | "id"
        | "nome"
        | "icone"
        | "peso"
        | "cor_tag"
        | "cor_fonte"
    >
>;
type Props = {
    field: ControllerRenderProps<{
        descricao?: string;
        cor_fonte?: string;
        cor_fundo?: string;
        local_atendimento?: string;
        orgao?: string;
        servicos?: string[];
        prioridades?: string[];
        logo?: File | string;
    }, "prioridades">;
    fieldState: ControllerFieldState;
    priorities?: Priorities
    style?: React.CSSProperties;
}

export default function PrioritiesField({ field, fieldState, style, priorities }: Props) {
    const [search, setSearch] = useState<string>();
    const [previousSelected, setPreviousSelected] = useState<Priorities>([]);
    const debouncedSearch = useDebouncedValue(search, 500);
    const { data, isLoading } = usePriorityList({
        search: debouncedSearch, page: 1, ativo: true,
    });

    const priorityList = useMemo(() => {
        return data?.results || [];
    }, [data]);

    const options = useMemo(() => {
        const merged = [...previousSelected, ...priorityList];
        return merged.filter(
            (item, index, self) => self.findIndex(i => i.id === item.id) === index
        );
    }, [priorityList, previousSelected]);

    function handleInputChange(_, value: string, reason: string) {
        if (reason === "input") {
            setSearch(value === "" ? undefined : value);
        }
    }

    function handleChange(_, newValue: Priority[]) {
        setSearch(undefined);
        setPreviousSelected(newValue);
        field.onChange(newValue.map(item => item.id.toString()));
    }

    useEffect(() => {
        if (priorities) {
            setPreviousSelected(priorities);
        }
    }, [priorities]);

    return (
        <Autocomplete
            multiple
            disableCloseOnSelect
            filterSelectedOptions
            style={style}
            freeSolo={false}
            options={options}
            value={options.filter((item) => field.value.includes(item.id.toString())) || []}
            inputValue={search || ""}
            onChange={handleChange}
            onInputChange={handleInputChange}
            getOptionLabel={(option) => option.nome}
            getOptionSelected={(option, value) => option.id === value.id}
            loading={isLoading}
            loadingText="Carregando prioridades..."
            noOptionsText="Nenhuma prioridade encontrada"
            renderInput={(params) => (
                <TextField
                    {...params}
                    required
                    label="Prioridades"
                    variant="outlined"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message || "Selecione as prioridades do totem"}
                    InputProps={{
                        ...params.InputProps,
                        required: false,
                        endAdornment: (
                            <>
                                {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
}
